import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

/**
 * One option in a select list. `value` and `label` are the contract every option source honours (the cfg-driven
 * filter-options RPC, lookups, static option arrays). `name` is the undecorated display name a few selects keep
 * beside a decorated `label` (a contact's name when the label carries "(email)"; the RPC emits it only when a
 * report's `select_fields` asks for it). `data` is the source record behind the option, for selects that need
 * more than value/label (city → country, contact → email); its shape depends on the select.
 *
 * Declared explicitly rather than inferred from the schema: with `strictNullChecks` off, Zod 4's inference marks
 * required keys optional (`value?`), which is how every option consumer ended up type-checking against a shape
 * nobody produces. Keep the interface and the schema below in step — the schema is the runtime validator.
 */
export interface SelectListEntry {
  value: string | number
  label: string
  name?: string
  data?: Record<string, any>
  // An option the user may see but not pick (AntD greys it out), with the
  // reason shown on hover - e.g. a pipeline stage only admins may move into
  // (core/hooks/useStageMoveRights).
  disabled?: boolean
  title?: string
}

export const SelectListEntrySchema = z
  .object({
    value: z.union([z.string(), z.number()]),
    label: z.string(),
    name: z.string().optional(),
    data: z.record(z.string(), z.any()).optional(),
    disabled: z.boolean().optional(),
    title: z.string().optional()
  })
  .strict()

const SelectListEntryFields = makeFieldKeys(SelectListEntrySchema)

function validateSelectListEntry(data: unknown): SelectListEntry {
  try {
    // The schema requires `value`; the assertion only papers over Zod 4 inferring it optional (see the interface).
    return validateWithZodSchema(SelectListEntrySchema, data, 'select list entry') as SelectListEntry
  } catch (error) {
    throw new Error(`Select list entry validation failed: ${error}`)
  }
}
